import { PlanningTableRowAction, PlanningTableRowActionProps, SelectionValidation } from "@mcleod/general/src/planning/PlanningTableRowAction";
import { Button, ClickEvent, ComponentCreator, Label, Panel, SlideoutDecorator, Snackbar } from "@mcleod/components";
import { OrderPlanningToolPanel } from "../OrderPlanningToolPanel";
import { ActionPermission, ModelRow, StringUtil } from "@mcleod/core";
import { RowDispatchControlFields } from "@mcleod/general/src/models/autogen/AutogenModelDispatchControl";

export interface OrderPlanningRowActionProps extends PlanningTableRowActionProps {
    afterSlideout?: (decorator: SlideoutDecorator) => void;
}

export type ValidationReason = {
    message: string;
    ids: string[];
};

export type ReasonMap = Map<string, ValidationReason>;

export abstract class OrderPlanningRowAction extends PlanningTableRowAction {
    private readonly _toolPanel: OrderPlanningToolPanel;
    public validationPromise: Promise<void> | null = null;

    public constructor(toolPanel: OrderPlanningToolPanel) {
        super();
        this._toolPanel = toolPanel;
        this.getButton().addClickListener((event: ClickEvent) => {
            try {
                const validationResults = this.validateSelection(this.toolPanel?.table?.selectedRows.map(tableRow => tableRow.data as ModelRow));
                if (validationResults instanceof Promise) {
                    this.validationPromise = validationResults.then((results: SelectionValidation) => {
                        if (!results.enabled) {
                            this.showSelectionValidationSnackbar(results);
                            return;
                        }
                        this.execute(this.getExecuteProps(event));
                    }).finally(() => {
                        this.validationPromise = null;
                    });
                    return;
                } else {
                    if (!validationResults.enabled) {
                        this.showSelectionValidationSnackbar(validationResults);
                        return;
                    }
                    this.execute(this.getExecuteProps(event));
                }
            } finally {
                this.toolPanel.actionsLayout.overlay?.remove();
            }
        })
        this.getButton().visible = true;
        this.onLayoutLoad();
    }

    private showSelectionValidationSnackbar(validation: SelectionValidation) {
        const label = new Label({
            id: "labelChangedDataWarning",
            text: "The selected rows' data has changed.",
            padding: 0,
        })
        const panel = this.buildDisabledReason(validation.reasons) as Panel;
        panel.insert(label, 0);
        Snackbar.showWarningSnackbar(panel);
    }

    /**
     * The timeline view will update its datasource only when certain fields have changed.  This is to help with
     * performance.  To ensure that the action is not using stale data, getFieldsUsedInThisFile() should return
     * the fields that the action needs to listen to for live updates.  This is any field used in actionValidateSelection
     * or execute.
     * @return An array of field names that this action uses in its logic.
     */
    abstract getFieldsUsedInThisFile(): string[];

    /**
     * This method should return the button that the action is for.  The abstract class will use it to setup the
     * click listener and validation.
     * @return The Button instance that the action will use.
     */
    abstract getButton(): Button;

    /**
     * This method should return the button's id that the action is for.
     * DO NOT USE THE getButton().id PROPERTY HERE.  This can get called before the button is created.
     * The tool panel class will use it to check permissions.
     * @return The button's id as a string.
     */
    abstract getButtonId(): string;

    /**
     * This method is called when new rows are selected.  If the reasonMap is empty, the action/button will be enabled.
     * If anything is added to the reasonMap, the button will be disabled and the reasonMap will be used to create a
     * disabledReason tooltip.
     * @param selectedRows The rows that are currently selected in the table.
     * @param reasonMap A map to store validation fail reasons.
     */
    protected abstract actionValidateSelection(selectedRows: ModelRow[], reasonMap: ReasonMap):  void | Promise<void>;

    /**
     * Override this method to do any setup that's needed when the layout is loaded.
     * @protected
     */
    protected onLayoutLoad() {}

    /**
     * DO NOT OVERRIDE THIS FUNCTION.  Override `getAdditionalProps()` instead.
     * Returns the props object that can be passed to the execute() method.
     * @param event
     */
    public getExecuteProps(event: ClickEvent): OrderPlanningRowActionProps {
        return {
            selectedRows: this.toolPanel.table.selectedRows,
            ...this.getAdditionalProps(event),
        }
    }

    get toolPanel(): OrderPlanningToolPanel {
        return this._toolPanel;
    }

    /**
     * Override this method to add additional properties to the props object passed to execute().
     * @param event The ClickEvent that triggered the action.
     * @return An object containing additional properties to be merged into the props object that's passed to execute().
     */
    protected getAdditionalProps(event: ClickEvent): Partial<OrderPlanningRowActionProps> {
        return {};
    }

    /**
     * Override this method to specify a permission name that will be checked when multiple rows are selected.
     * If a non-null value is return from this and the user does not have that permission, the action will be disabled
     * when multiple rows are selected.
     * @return The permission name to check for multi-select actions.
     */
    public getMultiSelectPermission(): ActionPermission | null {
        return null;
    }

    /**
     * This is a helper method to get the afterSlideout findNear prop if findNear exists.
     * @return An object containing the afterSlideout function if it exists, otherwise an empty object.
     */
    protected getFindNearAfterSlideoutProp(): Partial<OrderPlanningRowActionProps> {
        return {
            ...(this.toolPanel.findNearAction && {afterSlideout: this.toolPanel.findNearAction.afterSlideout}),
        }
    }

    /**
     * This method is called when row selections change.  It's primarily used if an action has code in
     * actionValidateSelection that needs to be run when the TableRow is deselected.
     * @return True if the action should run validation when a row is deselected.
     */
    deselectionAllowed(): boolean {
        return false;
    }

    /**
     * This method is used to determine the maximum number of TableRow selections allowed for this action.
     * It can return a hardcoded number, or a field name from dispatch_control.  If a field name is returned,
     * the action will check the value of that field in the dispatch_control table to determine the maximum number
     * of selections.
     * @return The maximum number of selections allowed, or a field name from RowDispatchControlFields.
     */
    getMaxSelections(): keyof RowDispatchControlFields | number {
        return 1;
    }

    /**
     * Do not override this method unless absolutely necessary.  Override actionValidateSelection instead.
     * This method is called from the tool panel when the selection changes.  It will call actionValidateSelection
     * and convert the results into a SelectionValidation object.
     * @param selectedRows The currently selected rows in the table.
     * @return A SelectionValidation object indicating whether the selection is valid and any reasons for being invalid.
     */
    public override validateSelection(selectedRows: ModelRow[]): SelectionValidation | Promise<SelectionValidation> {
        const reasonMap: ReasonMap = new Map();
        const validationResult: void | Promise<void> = this.actionValidateSelection(selectedRows, reasonMap);

        if (validationResult instanceof Promise) {
            return validationResult.then(_ => this.convertReasonMap(reasonMap));
        } else {
            return this.convertReasonMap(reasonMap);
        }
    }

    /**
     * Adds a reason to the reasonMap.  modelRow and arrayField are only needed if and array of IDs is needed in the
     * message.  See convertReasonMap for how the message is formatted and how modelRow and arrayField are used.
     * @param reasonMap The map to add the reason to.
     * @param key A key used to group multiple rows that fail the same validation check.
     * @param message The message to display for the reason.  It can include a placeholder `{0}` for the IDs.
     * @param modelRow The ModelRow that contains the data for the row that failed validation.  This is used to get
     * the IDs for the message format.
     * @param arrayField The field name in the ModelRow that contains the ID to be used in the message.
     * @protected
     */
    protected addReason(reasonMap: ReasonMap, key: string, message: string, modelRow?: ModelRow, arrayField?: string) {
        if (!reasonMap.has(key)) {
            reasonMap.set(key, {
                message: message,
                ids: []
            });
        }
        if (modelRow && arrayField)
            reasonMap.get(key)?.ids.push(modelRow.getString(arrayField));
    }

    /**
     * Converts the reasonMap into a SelectionValidation object.  The object is used to build a disabledReason tooltip.
     * @param reasonMap The map containing validation reasons.
     * @return A SelectionValidation object indicating whether the selection is valid and any reasons for being invalid.
     */
    private convertReasonMap(reasonMap: ReasonMap): SelectionValidation {
        const reasons: string[] = [];
        reasonMap.forEach((results: ValidationReason) => {
            if (results.ids.length > 0) {
                reasons.push(StringUtil.stringFormat(results.message, results.ids.join(", ")));
            } else {
                reasons.push(results.message);
            }
        });

        return {
            enabled: reasons.length === 0,
            reasons: reasons
        };
    }

    /**
     * Builds a Panel used in the disabledReason tooltip for the button.  Actions can override this method to customize
     * the tooltip if needed.  This will be called by the tool panel when the button is disabled.
     * @param reasons An array of reason strings used to generate the panel.
     * @return A button's tooltip takes a ComponentCreator, so that's what's returned here.
     */
    public buildDisabledReason(reasons: string[]): ComponentCreator {
        const panel = new Panel();
        reasons.forEach((reason, index) => {
            panel.add(new Label({
                id: `reason${index}`,
                text: reason,
                paddingLeft: 0
            }))
        })
        return panel;
    }

    /**
     * This is called when making the button visible.  If the button should check dispatch control, permissions, etc,
     * override this method and return a boolean indicating whether the action can be shown.
     * @return True if the action can be shown, false otherwise.
     */
    public static canShowAction(): boolean {
        return true;
    }

    /**
     * Override this to indicate that the action's visibility depends on a row-based check.
     * @return True if checkVisibility() should be called on selection change.
     */
    public isConditionallyVisible(): boolean {
        return false;
    }

    /**
     * Override this to perform a visibility check when a row is selected.
     * Only called if isConditionallyVisible() returns true.
     * @param modelRow The currently selected row.
     * @return True if the action should be visible, false otherwise.
     */
    public checkVisibility(modelRow: ModelRow): boolean {
        return true;
    }
}
